import { easings } from '@react-spring/shared';
import { useSpringRef, useSpring, animated } from '@react-spring/web';
import uniqueId from 'lodash/uniqueId';
import React, { MutableRefObject, useRef, useState } from 'react';
import { useClickAway, useMount } from 'react-use';
import classNames from 'classnames';

import { Nullable } from 'src/types/nullable.type';
import { trackSegmentEvent } from 'src/utils/segment';
import { ToggleComponent } from 'src/common/form/toggle/component';
import { Journey } from 'src/common/interfaces/journey.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useJourneys } from 'src/store/journeys';
import { apiUpdateJourney } from 'src/utils/journeyApi';
import { ControlPanelShell } from '../control-panel/shell';

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const StatusSelectionControlPanel = ({
  openRef,
  journey,
  isStatusEnabled,
  setIsStatusEnabled,
}: {
  openRef: MutableRefObject<() => void>;
  journey: Journey;
  isStatusEnabled: boolean;
  setIsStatusEnabled: (status: boolean) => void;
}) => {
  const { setSuccessNotification } = useNotificationMessage();
  const { updateJourney } = useJourneys();
  const [isOpen, setIsOpen] = useState(false);
  const [shellKey, setShellKey] = useState(uniqueId());

  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  useClickAway(containerElementRef, () => {
    setIsOpen(false);
  });

  useMount(() => {
    openRef.current = () => {
      setIsOpen(true);
      trackSegmentEvent('Status Clicked', {});
    };
  });

  const onChange = async (status: string) => {
    await apiUpdateJourney(journey.uuid, { status: status }).then(updateJourney);
    setSuccessNotification(`The journey has been updated.`);
  };

  const onStatusChanged = (checked: boolean) => {
    setIsStatusEnabled(checked);
    onChange(checked ? 'public' : 'draft');
  };

  const api = useSpringRef();
  const [styles] = useSpring(
    () => ({
      ref: api,
      from: { y: 8, opacity: 0 },
      to: { y: 0, opacity: 1 },
    }),
    []
  );

  React.useEffect(() => {
    if (isOpen) {
      api.start({ from: { y: 8, opacity: 0 }, to: { y: 0, opacity: 1 }, config: FLOAT_ANIMATION_CONFIG });
    } else {
      api.start({
        opacity: 0,
        config: { duration: 200, easing: easings.easeOutCubic },
        onRest: () => {
          setShellKey(uniqueId());
        },
      });
    }
  }, [isOpen]);

  const panelContentRef = React.useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerElementRef}
      className={classNames('absolute top-full mt-2 right-0', {
        'z-neue-color-panel-selected pointer-events-auto': isOpen,
        'z-neue-color-panel-unselected pointer-events-none': !isOpen,
      })}
    >
      <animated.div style={styles}>
        <ControlPanelShell
          title='Status'
          key={shellKey}
          size='large'
          active={isOpen}
          childrenContainerClassName='-mt-3'
        >
          <div className='flex flex-col gap-4' ref={panelContentRef}>
            <div className='text-neue-journey-small'>Set your Journey's status to published or draft</div>
            <ToggleComponent
              checked={isStatusEnabled}
              onChange={onStatusChanged}
              label={isStatusEnabled ? 'Published' : 'Draft'}
              labelClassName='text-neue-journey-fg text-neue-journey-small'
              isNeue={true}
            />
          </div>
        </ControlPanelShell>
      </animated.div>
    </div>
  );
};
